import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { useStoreDomain } from './hooks/useStoreDomain';
import { DashboardLayout } from './components/DashboardLayout';
import { FeatureGuard } from './components/FeatureGuard';
import { GoogleMapsProvider } from './components/maps/GoogleMapsProvider';
import { CartSidebar } from './components/CartSidebar';
import { StorefrontHead } from './components/StorefrontHead';
import './App.css';

// All page-level routes are code-split via React.lazy so the initial JS
// bundle stays small. Named exports are unwrapped via `.then(m => ({default: m.X}))`.
const named = (loader, key) => () => loader().then(m => ({ default: m[key] }));

const Home              = lazy(named(() => import('./pages/Home'), 'Home'));
const Login             = lazy(named(() => import('./pages/Auth'), 'Login'));
const Signup            = lazy(named(() => import('./pages/Auth'), 'Signup'));
const AuthCallback      = lazy(named(() => import('./pages/Auth'), 'AuthCallback'));
const StoreSetup        = lazy(named(() => import('./pages/StoreSetup'), 'StoreSetup'));
const Dashboard         = lazy(named(() => import('./pages/Dashboard'), 'Dashboard'));
const Products          = lazy(named(() => import('./pages/Products'), 'Products'));
const Inventory         = lazy(named(() => import('./pages/Inventory'), 'Inventory'));
const Categories        = lazy(named(() => import('./pages/Categories'), 'Categories'));
const Orders            = lazy(named(() => import('./pages/Orders'), 'Orders'));
const Customers         = lazy(named(() => import('./pages/Customers'), 'Customers'));
const FormSubmissions   = lazy(() => import('./pages/FormSubmissions'));
const Discounts         = lazy(named(() => import('./pages/Discounts'), 'Discounts'));
const AbandonedCarts    = lazy(named(() => import('./pages/AbandonedCarts'), 'AbandonedCarts'));
const Analytics         = lazy(named(() => import('./pages/Analytics'), 'Analytics'));
const Settings          = lazy(named(() => import('./pages/Settings'), 'Settings'));
const DeliveryZones     = lazy(named(() => import('./pages/DeliveryZones'), 'DeliveryZones'));
const POS               = lazy(named(() => import('./pages/POS'), 'POS'));
const KDS               = lazy(named(() => import('./pages/KDS'), 'KDS'));
const HotelReservations = lazy(named(() => import('./pages/HotelReservations'), 'HotelReservations'));
const StaffManagement   = lazy(() => import('./pages/StaffManagement'));
const QRCodeBuilder     = lazy(() => import('./pages/QRCodeBuilder'));
const Marketplace       = lazy(named(() => import('./pages/Marketplace'), 'Marketplace'));
const Storefront        = lazy(named(() => import('./pages/Storefront'), 'Storefront'));
const ProductDetail     = lazy(named(() => import('./pages/ProductDetail'), 'ProductDetail'));
const CategoryPage      = lazy(named(() => import('./pages/CategoryPage'), 'CategoryPage'));
const StorefrontPage    = lazy(() => import('./pages/StorefrontPage'));
const Checkout          = lazy(named(() => import('./pages/Checkout'), 'Checkout'));
const PaymentMethods    = lazy(named(() => import('./pages/PaymentMethods'), 'PaymentMethods'));
const OrderSuccess      = lazy(named(() => import('./pages/OrderSuccess'), 'OrderSuccess'));
const ForgotPassword    = lazy(named(() => import('./pages/ResetPassword'), 'ForgotPassword'));
const ResetPassword     = lazy(named(() => import('./pages/ResetPassword'), 'ResetPassword'));
const VerifyEmail       = lazy(() => import('./pages/VerifyEmail'));
const PlanSelection     = lazy(() => import('./pages/PlanSelection'));
const BillingSuccess    = lazy(() => import('./pages/BillingSuccess'));
const BillingCancelled  = lazy(() => import('./pages/BillingCancelled'));
const PublicPage        = lazy(() => import('./pages/PublicPage'));
const Profile           = lazy(() => import('./pages/Profile'));
const StoreContent      = lazy(() => import('./pages/StoreContent'));
const ThemePicker       = lazy(() => import('./pages/ThemePicker'));
const StorePublicPage   = lazy(() => import('./pages/StorePublicPage'));
const AIMenuImport      = lazy(() => import('./pages/AIMenuImport'));
const AICredits         = lazy(() => import('./pages/AICredits'));
const LoyaltyProgram    = lazy(() => import('./pages/LoyaltyProgram'));
const GiftCards         = lazy(() => import('./pages/GiftCards'));
const CustomDomains     = lazy(() => import('./pages/CustomDomains'));
const StorefrontPreview = lazy(() => import('./pages/StorefrontPreview'));
const PageBuilder       = lazy(() => import('./pages/PageBuilder'));
const Subscription      = lazy(() => import('./pages/Subscription'));
const Creators          = lazy(named(() => import('./pages/Creators'), 'Creators'));
const CustomerSessions  = lazy(() => import('./pages/CustomerSessions'));
const LiveDeliveries    = lazy(() => import('./pages/LiveDeliveries'));
const OfferPage         = lazy(() => import('./pages/OfferPage'));
const AllOffers         = lazy(() => import('./pages/AllOffers'));
const CustomerLoyalty   = lazy(() => import('./pages/CustomerLoyalty'));
const SeoSettings       = lazy(() => import('./pages/SeoSettings'));
const BranchManagement  = lazy(() => import('./pages/BranchManagement'));
const CreatorLogin      = lazy(() => import('./pages/CreatorLogin'));
const CreatorDashboard  = lazy(() => import('./pages/CreatorDashboard'));
// Admin pages
const AdminLogin        = lazy(named(() => import('./pages/admin/AdminLogin'), 'AdminLogin'));
const AdminLayout       = lazy(named(() => import('./pages/admin/AdminLayout'), 'AdminLayout'));
const AdminDashboard    = lazy(named(() => import('./pages/admin/AdminDashboard'), 'AdminDashboard'));
const AdminStores       = lazy(named(() => import('./pages/admin/AdminStores'), 'AdminStores'));
const AdminUsers        = lazy(named(() => import('./pages/admin/AdminUsers'), 'AdminUsers'));
const ContentManager    = lazy(named(() => import('./pages/admin/ContentManager'), 'ContentManager'));
const AdminThemes       = lazy(named(() => import('./pages/admin/AdminThemes'), 'AdminThemes'));
const AdminPricing      = lazy(() => import('./pages/admin/AdminPricing'));
const AdminFeatures     = lazy(() => import('./pages/admin/AdminFeatures'));
const AdminAIImports    = lazy(() => import('./pages/admin/AdminAIImports'));
const AdminDomains      = lazy(() => import('./pages/admin/AdminDomains'));
const AdminSubscriptions = lazy(() => import('./pages/admin/AdminSubscriptions'));
const AdminAnalytics    = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminSettings     = lazy(() => import('./pages/admin/AdminSettings'));
const AdminStaff        = lazy(() => import('./pages/admin/AdminStaff'));
const AdminSessions     = lazy(() => import('./pages/admin/AdminSessions'));
const AuditTrail        = lazy(() => import('./pages/admin/AuditTrail'));
const AdminAICredits    = lazy(() => import('./pages/admin/AdminAICredits'));
const DriverTracking    = lazy(() => import('./pages/DriverTracking'));
const CustomerTracking  = lazy(() => import('./pages/CustomerTracking'));
const CustomerAccount   = lazy(() => import('./pages/CustomerAccount'));
const NotificationPreferences = lazy(() => import('./pages/NotificationPreferences'));
const DriverLogin       = lazy(() => import('./pages/DriverLogin'));
const DriverDashboard   = lazy(() => import('./pages/DriverDashboard'));
const POSPortal         = lazy(() => import('./pages/POSPortal'));
const POSTerminal       = lazy(() => import('./pages/POSTerminal'));

// Tiny fullscreen fallback shown while a route's chunk is loading.
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background" data-testid="route-loading">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
  </div>
);

const PrivateRoute = ({ children }) => {
  const { user, loading, stores, storesLoaded } = useAuth();
  const location = useLocation();

  if (loading || !storesLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;

  // Always allow access to billing and utility pages
  const ungatedPaths = ['/select-plan', '/billing/success', '/billing/cancelled', '/profile', '/logout'];
  const isUngated = ungatedPaths.some(p => location.pathname.startsWith(p));
  if (isUngated) return children;

  // Gate 1: Force plan selection if no plan chosen
  if (!user.selected_plan) {
    return <Navigate to="/select-plan" replace />;
  }

  // Gate 2: For paid plans, block onboarding + dashboard until payment confirmed
  const isFree = user.selected_plan === 'free';
  const onboardingUnlocked = user.onboarding_unlocked !== false; // default true for backwards compat

  if (!isFree && !onboardingUnlocked) {
    // Paid plan without confirmed payment → send to billing verification
    if (location.pathname === '/store-setup' || location.pathname.startsWith('/dashboard')) {
      return <Navigate to="/billing/success" replace />;
    }
  }

  // Gate 3: Force onboarding if no stores exist (and onboarding is unlocked)
  if (stores.length === 0 && location.pathname !== '/store-setup') {
    return <Navigate to="/store-setup" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return !user ? children : <Navigate to="/dashboard" />;
};

/**
 * Global error boundary — catches JS errors at ANY level
 * and shows a friendly message instead of a blank page.
 */
class AppErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('App render error:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
          <div className="text-center px-4">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Quelque chose s'est mal passé</h1>
            <p className="text-gray-500 dark:text-gray-400 mb-4">Impossible de charger cette page. Veuillez actualiser la page.</p>
            <button onClick={() => window.location.reload()} className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600">
              Actualiser la page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}


/**
 * Storefront shell — renders when visiting a subdomain (myshop.woyof.com)
 * or a custom domain (shop.example.com).
 * Provides routes for the public storefront only.
 */
function StorefrontApp({ subdomain }) {
  // Swap PWA manifest for this merchant's storefront
  React.useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.href = `${process.env.REACT_APP_BACKEND_URL}/api/public/stores/${subdomain}/manifest.json`;
    // Register service worker for storefront PWA
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js').catch(() => {});
    }
    return () => { if (link) link.href = '/manifest.json'; };
  }, [subdomain]);

  return (
    <BrowserRouter>
      <StorefrontHead subdomain={subdomain} />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<><Storefront subdomainOverride={subdomain} /><CartSidebar subdomainOverride={subdomain} /></>} />
          <Route path="/product/:productSlug" element={<><ProductDetail subdomainOverride={subdomain} /><CartSidebar subdomainOverride={subdomain} /></>} />
          <Route path="/category/:categorySlug" element={<><CategoryPage subdomainOverride={subdomain} /><CartSidebar subdomainOverride={subdomain} /></>} />
          <Route path="/checkout" element={<Checkout subdomainOverride={subdomain} />} />
          <Route path="/order-success" element={<OrderSuccess subdomainOverride={subdomain} />} />
          <Route path="/offers" element={<AllOffers />} />
          <Route path="/offers/:offerSlug" element={<OfferPage />} />
          <Route path="/loyalty" element={<CustomerLoyalty subdomainOverride={subdomain} />} />
          <Route path="/account" element={<CustomerAccount subdomainOverride={subdomain} />} />
          <Route path="/page/:slug" element={<StorePublicPage subdomainOverride={subdomain} />} />
          <Route path="/track/driver/:token" element={<DriverTracking />} />
          <Route path="/track/order/:token" element={<CustomerTracking />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Toaster />
    </BrowserRouter>
  );
}

/**
 * Main platform app — renders on the main domain (woyof.com)
 */
/**
 * Wrapper that swaps PWA manifest for driver routes
 */
function DriverManifestWrapper({ children }) {
  React.useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.href = '/driver-manifest.json';
    return () => { if (link) link.href = '/manifest.json'; };
  }, []);
  return children;
}

/**
 * Layout wrapper for path-based storefront routes (/store/:subdomain/*).
 * Injects merchant title, favicon, and description into <head>.
 */
function StorefrontRouteLayout() {
  const { subdomain } = useParams();
  return (
    <>
      <StorefrontHead subdomain={subdomain} />
      <Outlet />
    </>
  );
}

function MainApp() {
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/marketplace" element={<Marketplace />} />

        {/* Storefront Routes (path-based fallback) — wrapped with merchant SEO */}
        <Route path="/store/:subdomain" element={<StorefrontRouteLayout />}>
          <Route index element={<><Storefront /><CartSidebar /></>} />
          <Route path="product/:productSlug" element={<><ProductDetail /><CartSidebar /></>} />
          <Route path="category/:categorySlug" element={<><CategoryPage /><CartSidebar /></>} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="order-success" element={<OrderSuccess />} />
          <Route path="offers" element={<AllOffers />} />
          <Route path="offers/:offerSlug" element={<OfferPage />} />
          <Route path="loyalty" element={<CustomerLoyalty />} />
          <Route path="page/:slug" element={<StorePublicPage />} />
          <Route path="account" element={<CustomerAccount />} />
        </Route>

        {/* Auth Routes */}
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Plan Selection */}
        <Route path="/select-plan" element={<PrivateRoute><PlanSelection /></PrivateRoute>} />

        {/* Billing Flow */}
        <Route path="/billing/success" element={<PrivateRoute><BillingSuccess /></PrivateRoute>} />
        <Route path="/billing/cancelled" element={<PrivateRoute><BillingCancelled /></PrivateRoute>} />

        {/* Profile */}
        <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />

        {/* CMS Public Pages */}
        <Route path="/page/:slug" element={<PublicPage />} />

        {/* Store Setup */}
        <Route path="/store-setup" element={<PrivateRoute><StoreSetup /></PrivateRoute>} />

        {/* Dashboard Routes */}
        <Route path="/dashboard" element={<PrivateRoute><DashboardLayout /></PrivateRoute>}>
          <Route index element={<Dashboard />} />
          <Route path="storefront" element={<PageBuilder />} />
          <Route path="storefront/theme" element={<StorefrontPreview />} />
          <Route path="products" element={<Products />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="categories" element={<Categories />} />
          <Route path="orders" element={<Orders />} />
          <Route path="customers" element={<Customers />} />
          <Route path="form-submissions" element={<FormSubmissions />} />
          <Route path="abandoned-carts" element={<AbandonedCarts />} />
          <Route path="coupons" element={<Discounts />} />
          <Route path="delivery-zones" element={<DeliveryZones />} />
          <Route path="branches" element={<BranchManagement />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="settings" element={<Settings />} />
          <Route path="pos" element={<FeatureGuard featureKey="pos" moduleName="Point de Vente"><POS /></FeatureGuard>} />
          <Route path="kds" element={<FeatureGuard featureKey="kds" moduleName="Écran Cuisine"><KDS /></FeatureGuard>} />
          <Route path="hotel" element={<FeatureGuard featureKey="hotel" moduleName="Réservations Hôtel"><HotelReservations /></FeatureGuard>} />
          <Route path="staff" element={<StaffManagement />} />
          <Route path="qr-codes" element={<QRCodeBuilder />} />
          <Route path="payments" element={<PaymentMethods />} />
          <Route path="content" element={<StoreContent />} />
                    <Route path="themes" element={<ThemePicker />} />
          <Route path="ai-import" element={<FeatureGuard featureKey="ai_import" moduleName="AI Import"><AIMenuImport /></FeatureGuard>} />
          <Route path="ai-credits" element={<AICredits />} />
          <Route path="loyalty" element={<LoyaltyProgram />} />
          <Route path="gift-cards" element={<GiftCards />} />
          <Route path="domains" element={<CustomDomains />} />
          <Route path="seo" element={<SeoSettings />} />
          <Route path="subscription" element={<Subscription />} />
          <Route path="creators" element={<Creators />} />
          <Route path="live-customers" element={<CustomerSessions />} />
          <Route path="live-deliveries" element={<LiveDeliveries />} />
          <Route path="notification-preferences" element={<NotificationPreferences />} />
        </Route>

        {/* Creator Portal Routes (public, no merchant auth) */}
        <Route path="/creator/login" element={<CreatorLogin />} />
        <Route path="/creator/reset-password" element={<CreatorLogin />} />
        <Route path="/creator/dashboard" element={<CreatorDashboard />} />

        {/* POS Portal Routes (standalone, no auth wrapper) */}
        <Route path="/pos/:subdomain" element={<POSPortal />} />
        <Route path="/pos/:subdomain/terminal" element={<POSTerminal />} />

        {/* Driver Portal Routes */}
        <Route path="/driver/login" element={<DriverManifestWrapper><DriverLogin /></DriverManifestWrapper>} />
        <Route path="/driver/dashboard" element={<DriverManifestWrapper><DriverDashboard /></DriverManifestWrapper>} />

        {/* Live Delivery Tracking (public, token-based) */}
        <Route path="/track/driver/:token" element={<DriverTracking />} />
        <Route path="/track/order/:token" element={<CustomerTracking />} />

        {/* Super Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="stores" element={<AdminStores />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="pricing" element={<AdminPricing />} />
          <Route path="features" element={<AdminFeatures />} />
          <Route path="content" element={<ContentManager />} />
                    <Route path="themes" element={<AdminThemes />} />
          <Route path="ai-imports" element={<AdminAIImports />} />
          <Route path="domains" element={<AdminDomains />} />
          <Route path="subscriptions" element={<AdminSubscriptions />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="staff" element={<AdminStaff />} />
          <Route path="sessions" element={<AdminSessions />} />
          <Route path="audit-trail" element={<AuditTrail />} />
          <Route path="ai-credits" element={<AdminAICredits />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>
      </Routes>
      </Suspense>
      <Toaster />
    </BrowserRouter>
  );
}

function AppInner() {
  const { isStorefront, subdomain, loading } = useStoreDomain();

  // Consume `#_imp=...&_impuser=...` hash if present (impersonation handoff fallback).
  // Runs once before any auth provider mounts so the token lands in sessionStorage
  // before AuthContext reads it.
  if (typeof window !== 'undefined' && window.location.hash.includes('_imp=')) {
    try {
      const hash = window.location.hash.replace(/^#/, '');
      const params = new URLSearchParams(hash);
      const tok = params.get('_imp');
      const usr = params.get('_impuser');
      if (tok) {
        sessionStorage.setItem('token', tok);
        sessionStorage.setItem('isImpersonating', 'true');
        if (usr) sessionStorage.setItem('impersonatedUser', usr);
        // Strip the hash before React initializes routing
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    } catch { /* silent */ }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <CartProvider>
          {isStorefront ? (
            <StorefrontApp subdomain={subdomain} />
          ) : (
            <MainApp />
          )}
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <GoogleMapsProvider>
        <AppErrorBoundary>
          <AppInner />
        </AppErrorBoundary>
      </GoogleMapsProvider>
    </HelmetProvider>
  );
}

export default App;
